/**
 * RunActivityPanel — the left rail of the run detail lockup.
 *
 * Three synced views of "what happened", tabbed:
 *  - Summary  — failure triage first (headline + assertion summary), then the
 *               per-step summary shared with the flow-designer run panel.
 *  - Timeline — the navigable browser-action timeline locked up with the video.
 *  - Chat     — the run investigator.
 *
 * Summary is the failure-triage entry point: a failed run leads with the red
 * "what failed" headline and a one-click path to the root-cause analysis in
 * the right column (jump when it exists, trigger-and-jump when it doesn't).
 *
 * @module ui/pages/RunDetailPage/RunActivityPanel
 */

import React from 'react';
import { ListChecks, FilmStrip, ChatCircleDots } from '@phosphor-icons/react';
import {
  BrowserActionsTimeline,
  UnderlineTabs,
  UnderlineTab,
  type StepCacheMetadata,
} from '@chatsdet/ui';
import type {
  VideoTimeline,
  WorkflowAssertionResult,
  WorkflowAssertionSummary,
} from '@chatsdet/types';
import type { FailureDiagnostic } from '@chatsdet/types/issues';
import type {
  WorkflowRunStepDto,
  SerializedTrackedVariableDto,
} from '../FlowDesigner/hooks/useWorkflowRun';
import type { WorkflowNode } from '../../../types/workflow';
import { RunStepList } from '../FlowDesigner/runs/RunStepList';
import { RunVariablesPanel } from '../FlowDesigner/runs/RunVariablesPanel';
import {
  RunFailureHeadline,
  findFailedRunStep,
  getFailedStepData,
  type FailureHeadlineAction,
} from '../FlowDesigner/runs/RunFailureHeadline';
import { DiagnosticHeadline } from '../FlowDesigner/runs/DiagnosticHeadline';
import { AssertionSummaryCard, type AssertionIssueLinkContext } from '../FlowDesigner/runs/StepAssertions';
import { CodeFlowRunSteps } from '../../../features/code-flows';
import { LegacyChatTombstone } from '../../../features/assistant/LegacyChatTombstone';

export type ActivityTab = 'summary' | 'timeline' | 'chat';

export interface RunActivityPanelProps {
  steps: WorkflowRunStepDto[];
  nodes: WorkflowNode[];
  /** True when `nodes` come from the run-pinned snapshot (see useRunSnapshotNodes). */
  nodesFromSnapshot?: boolean;
  timeline: VideoTimeline | null;
  currentTimeMs: number;
  onSeek: (timestampMs: number) => void;
  stepMetadata?: Record<string, StepCacheMetadata>;
  /** Bumped on a direct user action so the Timeline reclaims auto-follow. */
  resyncSignal?: number;
  /** Controlled active tab (lifted to the page so it can widen the rail for Chat). */
  selectedTab: ActivityTab;
  onTabChange: (tab: ActivityTab) => void;
  /** Workflow identifier for the failure-diagnostic headline. */
  workflowId: string | null;
  /** The run being shown; code-flow runs load their step rows by it. */
  runId: string;
  /** "Link issue" context for failing assertions (see `useAssertionIssueLinkContext`). */
  issueLinkContext: AssertionIssueLinkContext | null;
  /** Failure-triage context (full-page run detail only). */
  runStatus?: string;
  runErrorMessage?: string | null;
  /** Legacy automatic recovery erased the persisted step rows for this run. */
  stepHistoryUnavailable?: boolean;
  assertionSummary?: WorkflowAssertionSummary | null;
  /** Tracked variables captured during the run, shown as a collapsible panel in Summary. */
  trackedVariables?: SerializedTrackedVariableDto[];
  /** Failure-diagnostic state, for the headline's root-cause action. */
  diagnosticStatus?: string | null;
  /** Completed diagnostic — when present, the Summary leads with its headline. */
  diagnostic?: FailureDiagnostic | null;
  onDiagnose?: () => void;
  isDiagnosing?: boolean;
  /** Smooth-scrolls the right column to the root-cause analysis section. */
  onJumpToRootCause?: () => void;
  /** Seek the video to a root-cause event (and switch the scrubber to the diagnostic timeline). */
  onSelectDiagnosticEvent?: (timestampMs: number) => void;
  className?: string;
}

export const RunActivityPanel: React.FC<RunActivityPanelProps> = ({
  steps,
  nodes,
  nodesFromSnapshot,
  timeline,
  currentTimeMs,
  onSeek,
  stepMetadata,
  resyncSignal,
  selectedTab,
  onTabChange,
  workflowId,
  runId,
  issueLinkContext,
  runStatus,
  runErrorMessage,
  stepHistoryUnavailable,
  assertionSummary,
  trackedVariables,
  diagnosticStatus,
  diagnostic,
  onDiagnose,
  isDiagnosing,
  onJumpToRootCause,
  onSelectDiagnosticEvent,
  className,
}) => {
  const tab = selectedTab;

  const failedStep = React.useMemo(() => {
    if (runStatus !== 'failed') return null;
    const step = findFailedRunStep(steps);
    return step ? getFailedStepData(step, nodes, steps) : null;
  }, [runStatus, steps, nodes]);

  const rootCauseAction = ((): FailureHeadlineAction | undefined => {
    if (!onJumpToRootCause) return undefined;
    if (diagnosticStatus === 'completed') {
      return { label: 'Jump to root cause', onPress: onJumpToRootCause };
    }
    if (diagnosticStatus === 'pending' || diagnosticStatus === 'running') {
      return { label: 'View analysis', onPress: onJumpToRootCause };
    }
    if (!onDiagnose) return undefined;
    return {
      label: isDiagnosing ? 'Starting analysis…' : 'Diagnose root cause',
      isDisabled: isDiagnosing,
      onPress: () => {
        onDiagnose();
        onJumpToRootCause();
      },
    };
  })();

  const assertionResults = React.useMemo(
    () =>
      steps.flatMap(
        (s) => (s.metadataJson?.assertionResults ?? []) as WorkflowAssertionResult[]
      ),
    [steps]
  );

  return (
    <div className={`flex min-h-0 flex-col bg-card ${className ?? ''}`}>
      <UnderlineTabs
        selectedId={tab}
        onSelectionChange={(id) => onTabChange(id as ActivityTab)}
        layoutId="run-activity"
        className="shrink-0 gap-2 px-3 pt-2"
      >
        <UnderlineTab id="summary" className="px-2 py-2.5">
          <span className="flex items-center gap-1.5">
            <ListChecks size={16} weight={tab === 'summary' ? 'fill' : 'regular'} />
            Summary
          </span>
        </UnderlineTab>
        <UnderlineTab id="timeline" className="px-2 py-2.5">
          <span className="flex items-center gap-1.5">
            <FilmStrip size={16} weight={tab === 'timeline' ? 'fill' : 'regular'} />
            Timeline
          </span>
        </UnderlineTab>
        <UnderlineTab id="chat" className="px-2 py-2.5">
          <span className="flex items-center gap-1.5">
            <ChatCircleDots size={16} weight={tab === 'chat' ? 'fill' : 'regular'} />
            Chat
          </span>
        </UnderlineTab>
      </UnderlineTabs>

      {/* Panels */}
      <div className="min-h-0 flex-1">
        <div className={`h-full ${tab === 'summary' ? 'overflow-y-auto px-3 py-2' : 'hidden'}`}>
          {runStatus === 'failed' &&
            (diagnosticStatus === 'completed' && diagnostic ? (
              // Full-bleed tinted band (edge-to-edge in the rail) instead of a
              // contained card, so the diagnosis isn't squashed by inset padding.
              <div className="-mx-3 -mt-2 mb-3 border-b border-border bg-muted/20 px-3 pb-3 pt-3">
                <DiagnosticHeadline
                  diagnostic={diagnostic}
                  workflowId={workflowId}
                  onSelectEvent={onSelectDiagnosticEvent}
                  onViewAnalysis={onJumpToRootCause}
                />
              </div>
            ) : (
              <div className="mb-3">
                <RunFailureHeadline
                  failedStep={failedStep}
                  runErrorMessage={runErrorMessage}
                  action={rootCauseAction}
                />
              </div>
            ))}
          {assertionSummary && (
            <div className="mb-3">
              <AssertionSummaryCard summary={assertionSummary} results={assertionResults} />
            </div>
          )}
          <CodeFlowRunSteps runId={runId}>
              <RunStepList
                steps={steps}
                nodes={nodes}
                workflowId={workflowId}
                runId={runId}
                nodesFromSnapshot={nodesFromSnapshot}
                timeline={timeline}
                onSeekToStep={onSeek}
                stepHistoryUnavailable={stepHistoryUnavailable}
                issueLinkContext={issueLinkContext ?? undefined}
              />
            </CodeFlowRunSteps>
          {trackedVariables && trackedVariables.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <RunVariablesPanel variables={trackedVariables} nodes={nodes} />
            </div>
          )}
        </div>
        <div className={`h-full ${tab === 'timeline' ? '' : 'hidden'}`}>
          <BrowserActionsTimeline
            timeline={timeline}
            currentTimeMs={currentTimeMs}
            onSeek={onSeek}
            stepMetadata={stepMetadata}
            resyncSignal={resyncSignal}
            className="h-full"
          />
        </div>
        <div className={`h-full ${tab === 'chat' ? '' : 'hidden'}`}>
          <LegacyChatTombstone
            className="m-3 h-[calc(100%-1.5rem)]"
            title="Run investigator chat has moved"
            description="Use the page-aware Assistant to investigate this run from anywhere."
          />
        </div>
      </div>
    </div>
  );
};
